import { useEffect, useRef, useState, type ReactNode, type FormEvent } from "react";
import { ArrowRight, CalendarClock, FileText, MapPin, ShieldCheck, type LucideIcon } from "lucide-react";

import { FINANCE_CALCULATOR_PATH } from "@/content/finance";
import { FINANCING_I4F_HOW_IT_WORKS_PATH } from "@/content/financing-i4f-how-it-works";
import { financingLp } from "@/content/landing/financing";
import { Ideal4FinancePartnerBanner } from "@/components/financing/ideal4finance-partner-banner";
import {
  FinanceOptionsAccordion,
  type FinanceAccordionPanel,
} from "@/components/finance-options-accordion";
import { useFinancePostcode } from "@/context/finance-postcode-context";

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

const DEFAULT_BENEFITS: { Icon: LucideIcon; title: string; body: string }[] = [
  {
    Icon: CalendarClock,
    title: "Monthly Payment Options",
    body: "Spread the cost over an agreed term with a fixed monthly repayment.",
  },
  {
    Icon: FileText,
    title: "Full Terms Up Front",
    body: "Every agreement shows the complete cost and term before you commit.",
  },
  {
    Icon: ShieldCheck,
    title: "No Obligation",
    body: "Finance is a payment option, not a requirement. You can pay for your installation without it.",
  },
];

const DEFAULT_ACCORDION_PANELS: FinanceAccordionPanel[] = [
  {
    Icon: FileText,
    title: "How It Works",
    body: "Finance is a regulated credit agreement arranged through Ideal4Finance. You apply directly, the agreement is subject to status, and you'll see the full terms, including the total amount repayable, before you sign anything.",
    tone: "primary",
  },
  {
    Icon: CalendarClock,
    title: "Your Term, Your Choice",
    body: "Repayment terms are agreed at application. A shorter term means higher monthly repayments over less time; a longer term means lower monthly repayments over more time.",
    tone: "accent",
  },
  {
    Icon: ShieldCheck,
    title: "Savings And Repayments Are Separate",
    body: "Your monthly repayments are fixed by your agreement and are due every month. Any savings estimates in your proposal are estimates, not guarantees, and your repayments don't change with them.",
    tone: "primary",
  },
];

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

export interface FinanceOptionsSectionProps {
  sourcePage?: string;
  serviceLabel?: string;
  eyebrow?: string;
  headline?: ReactNode;
  intro?: string;
  bannerClassName?: string;
  benefits?: { Icon: LucideIcon; title: string; body: string }[];
  accordionPanels?: FinanceAccordionPanel[];
  onPostcodeSubmit?: (postcode: string) => void;
}

export function FinanceOptionsSection({
  sourcePage = "/financing",
  serviceLabel = "Financing",
  eyebrow = "£ Finance",
  headline = (
    <>
      Finance Options:{" "}
      <br />
      <span className="font-normal">Spread The Cost</span>
    </>
  ),
  intro = financingLp.heroSub,
  bannerClassName = "my-4 md:my-2 md:float-right md:ml-6 md:mb-3 md:w-72 lg:w-80",
  benefits = DEFAULT_BENEFITS,
  accordionPanels = DEFAULT_ACCORDION_PANELS,
  onPostcodeSubmit,
}: FinanceOptionsSectionProps) {
  const panel = useReveal<HTMLDivElement>();
  const { setPostcode: setFinancePostcode } = useFinancePostcode();
  const [postcode, setPostcode] = useState("");
  const [postcodeError, setPostcodeError] = useState<string | null>(null);

  const handleEligibilitySubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const cleaned = postcode.trim();
    if (!cleaned) {
      setPostcodeError("Please enter your postcode.");
      return;
    }
    if (!UK_POSTCODE.test(cleaned)) {
      setPostcodeError("Please enter a valid UK postcode.");
      return;
    }
    setPostcodeError(null);
    const normalised = cleaned.toUpperCase();
    setFinancePostcode(normalised);
    onPostcodeSubmit?.(normalised);
    const contactEl = document.getElementById("contact");
    if (contactEl) {
      contactEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="finance-options" className="border-b border-border/40 bg-background py-12 md:py-16 scroll-mt-32">
      <div
        ref={panel.ref}
        className={`transition-all duration-500 ease-out motion-reduce:transition-none ${
          panel.shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        }`}
      >
        <div className="container-page">
          <div className="pt-2 pb-6 md:pb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">{eyebrow}</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight max-w-3xl">
              {headline}
            </h2>
            <div className="max-w-3xl">
              <p className="mt-3 md:mt-4 text-sm md:text-base text-foreground/80 leading-relaxed">
                {intro}
              </p>
              <Ideal4FinancePartnerBanner className={bannerClassName} />
              <p className="mt-3 md:mt-4 text-sm md:text-base text-foreground/80 leading-relaxed">
                Choose a term that suits your household, see the full agreement before you commit, and
                pay a fixed monthly repayment for the length of the term.
              </p>
              <div className="clear-both" />
            </div>
          </div>
        </div>

        <div className="border-y border-border/60 bg-card/40 py-5 md:py-6">
          <div className="container-page">
            <FinanceOptionsAccordion panels={accordionPanels} ariaLabel="How finance works" />
          </div>
        </div>

        <div className="border-b border-border/60 bg-background py-6 md:py-8">
          <div className="container-page">
            <div className="max-w-3xl">
              <h3 className="text-xl md:text-2xl font-bold text-foreground leading-tight">
                Your Payment Options
              </h3>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {benefits.map(({ Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-border bg-card p-5 shadow-soft"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <h4 className="mt-3 text-sm md:text-base font-semibold text-foreground leading-snug">
                    {title}
                  </h4>
                  <p className="mt-1.5 text-[13px] md:text-sm text-muted-foreground leading-relaxed">
                    {body}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6">
              <a
                href={FINANCE_CALCULATOR_PATH}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
              >
                Work out the monthly payments
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
              <a
                href={FINANCING_I4F_HOW_IT_WORKS_PATH}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
              >
                See how applying works
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>
        </div>

        <div className="bg-background py-6 md:py-8">
          <div className="container-page">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-xl md:text-2xl font-bold text-foreground leading-tight">
                Find Out If You&apos;re Eligible
              </h3>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                Enter your postcode below to check your eligibility and explore finance options for your installation.
              </p>
              <form
                onSubmit={handleEligibilitySubmit}
                className="mt-5 flex flex-col sm:flex-row items-stretch gap-3"
                noValidate
              >
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <MapPin className="h-4 w-4" aria-hidden />
                  </span>
                  <input
                    type="text"
                    name="postcode"
                    value={postcode}
                    onChange={(e) => {
                      setPostcode(e.target.value);
                      if (postcodeError) setPostcodeError(null);
                    }}
                    placeholder="Enter your postcode"
                    className="w-full min-h-11 rounded-full border border-border bg-background pl-9 pr-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                    aria-label="Enter your postcode"
                    aria-invalid={postcodeError ? "true" : "false"}
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-accent text-accent-foreground px-6 py-3.5 text-sm font-semibold shadow-cta hover:bg-accent/90 transition-colors active:scale-[0.98] whitespace-nowrap w-full sm:w-auto"
                >
                  Find Out If You&apos;re Eligible
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </form>
              {postcodeError && (
                <p className="mt-2 text-sm text-destructive" role="alert">
                  {postcodeError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
